import React from "react";
import classNames from "classnames";
import { L10n, L10nId } from "@fidget/common/i18n";
import styles from "../EditableDeleteForm/editableDeleteForm.css";
import actionStyles from "../deleteActionForm/deleteActionForm.css";
import { VerticalSeparator } from "../separators/verticalSeparator/verticalSeparator";
import { ConfirmationPrompt } from "../base/confirmationPrompt";
import { DeleteConfirmation } from "../base/deleteConfirmation";
import { deletingFormState } from "../base/deletingFormState";

interface Props {
  onDelete: () => Promise<void>;
  removeFromUi: () => void;
  deleteDescriptionId: L10nId;
  deleteConfirmationId: L10nId;
}

/**
 * Full width form rendering children
 * - ends with separator and delete callback button
 * - changes to confirmation style before delete
 * - displays delete confirmation after delete and disappears after timeout
 */
export const DeletableForm = ({
  deleteDescriptionId,
  onDelete,
  removeFromUi,
  deleteConfirmationId,
  children
}: React.PropsWithChildren<Props>) => {
  const { onRemove, wasDeleteClicked, setWasDeleteClicked, removeFromUiTimer } = deletingFormState({
    onDelete,
    removeFromUi
  });

  const renderFormEnding = () => (
    <div className={classNames(styles.container, actionStyles.container)}>
      <div className={styles.content}>
        <div className={styles.form}>
          {children}
          <div className={classNames(styles["form-actions"])}>
            <VerticalSeparator style="margin-none" />
            <div className={classNames(styles["discard-transitions"], styles.remove)}>
              <button
                className={styles["discard-button"]}
                type={"button"}
                onClick={() => setWasDeleteClicked(true)}
              >
                <i className="icon-close-cross" />
                <L10n id={"popup.button.delete"} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeleteConfirmationPrompt = (deleteCallback: () => void, textId: L10nId) => (
    <ConfirmationPrompt
      textId={textId}
      onCancel={() => setWasDeleteClicked(false)}
      onConfirm={deleteCallback}
    />
  );

  const renderContent = () => {
    if (wasDeleteClicked) {
      if (removeFromUiTimer === undefined) {
        return renderDeleteConfirmationPrompt(onRemove, deleteDescriptionId);
      }

      return <DeleteConfirmation textId={deleteConfirmationId} />;
    }

    return renderFormEnding();
  };

  return renderContent();
};
